import { useState } from 'react';
import { useListPicks, ListPicksPlatform } from '@workspace/api-client-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PlatformBadge } from '@/components/platform-badge';
import { RecommendationBadge } from '@/components/recommendation-badge';
import { RiskBadge } from '@/components/risk-badge';
import { ConfidenceBar } from '@/components/ui/confidence-bar';
import { Spinner } from '@/components/ui/spinner';
import { Star } from 'lucide-react';

export default function PicksPage() {
  const [platform, setPlatform] = useState<ListPicksPlatform | undefined>(undefined);

  const { data: picksData, isLoading } = useListPicks({
    limit: 50,
    platform,
  });
  const picks = picksData?.picks;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 pb-24 lg:pb-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Top AI Picks</h1>
        <p className="text-muted-foreground mt-1">
          Highest-confidence analysis from Claude across all markets
        </p>
      </div>

      {/* Platform Filter */}
      <div className="flex gap-2">
        <Button
          variant={platform === undefined ? 'default' : 'outline'}
          size="sm"
          onClick={() => setPlatform(undefined)}
        >
          All Platforms
        </Button>
        <Button
          variant={platform === 'polymarket' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setPlatform('polymarket')}
        >
          Polymarket
        </Button>
        <Button
          variant={platform === 'kalshi' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setPlatform('kalshi')}
        >
          Kalshi
        </Button>
      </div>

      {/* Picks Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6 space-y-4">
                <div className="h-6 bg-muted rounded w-24" />
                <div className="h-12 bg-muted rounded" />
                <div className="h-16 bg-muted rounded" />
                <div className="h-4 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : picks && picks.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {picks!.map((pick, idx) => (
            <Card key={idx} className="card-elevated border-0">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start justify-between">
                  <PlatformBadge platform={pick.platform} />
                  <RecommendationBadge
                    recommendation={pick.recommendation as 'YES' | 'NO'}
                    size="md"
                  />
                </div>

                <h3 className="font-semibold line-clamp-2 min-h-[3rem]">
                  {pick.marketQuestion}
                </h3>

                <ConfidenceBar confidence={pick.confidence} />

                {pick.riskLevel && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Risk Level</span>
                    <RiskBadge risk={pick.riskLevel as any} />
                  </div>
                )}

                {pick.edgeSummary && (
                  <p className="text-sm text-muted-foreground line-clamp-3">
                    {pick.edgeSummary}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Star className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="font-semibold mb-2">No picks available</h3>
            <p className="text-sm text-muted-foreground">
              Check back soon for new AI-powered market analysis
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
